import { type ForwardedRef, forwardRef, useEffect } from 'react';

import { type ListLocationsQuery, type ListLocationsQueryVariables, useListLocationsQuery } from '@/common/graphql';

import type { ComboboxItemWithFormatted, CommonProps, UseQueryReturn } from '../props';
import { SelectMultiple, type SelectMultipleProps } from '../SelectMultiple';
import { SelectSingle, type SelectSingleProps } from '../SelectSingle';

type Variables = Partial<Omit<ListLocationsQueryVariables, 'pageNumber' | 'pageSize' | 'keyword'>>;
type Data = ComboboxItemWithFormatted &
  Omit<ListLocationsQuery['listLocations']['items'][number], 'id' | 'name' | 'enabled'>;
type Props = CommonProps<Variables>;

const useQuery = (
  variables: Variables | undefined,
  onLoadingCompleted?: () => void,
): UseQueryReturn<ListLocationsQueryVariables, Data> => {
  const { data, ...rest } = useListLocationsQuery({
    variables: {
      pageNumber: null,
      pageSize: null,
      keyword: null,
      enabled: variables?.enabled ?? null,
      hasEmployees: variables?.hasEmployees ?? null,
      onlyIds: variables?.onlyIds ?? null,
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (onLoadingCompleted) onLoadingCompleted();
  }, [onLoadingCompleted]);

  return {
    data:
      data?.listLocations?.items?.map(it => ({
        ...it,
        value: it.id,
        label: it.name,
        disabled: !it.enabled,
      })) || [],
    ...rest,
  };
};

export const LocationSelectSingle = forwardRef(
  (props: Props & SelectSingleProps<Data>, ref: ForwardedRef<HTMLDivElement>) => {
    const { data, loading } = useQuery(props.variables, props.onLoadingCompleted);
    return <SelectSingle ref={ref} {...props} data={data} loading={loading} />;
  },
);

export const LocationSelectMultiple = (props: Props & SelectMultipleProps<Data>) => {
  const { data, loading } = useQuery(props.variables, props.onLoadingCompleted);
  return (
    <SelectMultiple
      multipleSelectedLabel={length => `${length} office locations`}
      {...props}
      data={data}
      loading={loading}
    />
  );
};
